import { Box, Text, Toast, useToast } from '@zillow/constellation-10';
import { PageViews } from './Graphs/PageViews';
import { Impressions } from './Graphs/Impressions';
import { ActiveProperties } from './Graphs/ActiveProperties';
import { Prospects } from './Graphs/Prospects';
import { FunnelBreakdown } from './Graphs/FunnelBreakdown';
import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import { useMemo } from 'react';
import { CustomerAnalyticsPage } from '../../../SharedComponents/CustomerAnalyticsPage';
import { OverviewReportSidebar } from './OverviewReportSidebar';
import type { FileType, SelectedGraphs } from '../../../SharedComponents/ExportDialog';
import { extractOverviewData, getOverviewGraphConfigs } from './OverviewReport.export';
import { useGetRegionPerformance } from './OverviewReport.utils';
import { BuildingType } from './OverviewReport.types';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { downloadExcel } from '../../../../utils/exportUtils';
import { downloadPDF } from '../../../../utils/pdfExportUtils';
import { captureGraphs } from '../../../../utils/captureGraphs';
import { buildCoverPageData } from '../../../../utils/buildCoverPageData';
import { emitOverviewExportEvent, emitOverviewExportPdfEvent } from '../../../../analytics/events';
import { useOverviewFilters } from './OverviewReport.hooks';
import { isAllSingleFamily as checkAllSingleFamily } from '../../../../utils/isAllSingleFamily';
import {
  generateActivePropertiesData,
  generateImpressionsData,
  generatePageViewsData,
  generateProspectsData,
  generateProspectsPackageBreakdownData,
} from './OverviewReport.data';

export const OverviewReport = () => {
  const { selectedRegionId, selectedFilters, filteredProperties, regionsMapping, selectedMarketId } = useMainLayout();
  const { user } = useRentalManagerUser();
  const { enqueueToast } = useToast();

  const {
    prospectsBuildingType,
    setProspectsBuildingType,
    prospectsDisplayType,
    setProspectsDisplayType,
    pageViewsBuildingType,
    setPageViewsBuildingType,
    pageViewsDisplayType,
    setPageViewsDisplayType,
    impressionsBuildingType,
    setImpressionsBuildingType,
    impressionsDisplayType,
    setImpressionsDisplayType,
    activePropertiesBuildingType,
    setActivePropertiesBuildingType,
    activePropertiesDisplayType,
    setActivePropertiesDisplayType,
  } = useOverviewFilters();

  const selectedProperty = filteredProperties.find(
    (property) => property.propertyDetails.listingAlias === selectedFilters.listingAlias,
  );

  const derivedSelectedMarketId = useMemo(() => {
    if (selectedProperty) {
      if (
        ![
          selectedProperty.propertyDetails.stateRegionId,
          selectedProperty.propertyDetails.metroStatRegionId,
          selectedProperty.propertyDetails.cityRegionId,
          selectedProperty.propertyDetails.zipRegionId,
        ].includes(selectedMarketId ?? 0)
      ) {
        return (
          selectedProperty.propertyDetails.stateRegionId ||
          selectedProperty.propertyDetails.metroStatRegionId ||
          selectedProperty.propertyDetails.cityRegionId ||
          selectedProperty.propertyDetails.zipRegionId
        );
      }

      return (
        selectedMarketId ||
        selectedProperty.propertyDetails.stateRegionId ||
        selectedProperty.propertyDetails.metroStatRegionId ||
        selectedProperty.propertyDetails.cityRegionId ||
        selectedProperty.propertyDetails.zipRegionId
      );
    }

    return selectedRegionId;
  }, [selectedMarketId, selectedProperty, selectedRegionId]);

  const allSingleFamily = checkAllSingleFamily(filteredProperties);
  const prospectsEffectiveBuildingType = allSingleFamily ? BuildingType.SINGLE_FAMILY : prospectsBuildingType;

  const { data: mfRegionPerformance, isLoading: isMfRegionPerformanceLoading } = useGetRegionPerformance({
    zillowRegionId: selectedMarketId,
    isSingleFamily: false,
    enabled: prospectsEffectiveBuildingType === BuildingType.MULTIFAMILY,
  });

  const { data: sfRegionPerformance, isLoading: isSfRegionPerformanceLoading } = useGetRegionPerformance({
    zillowRegionId: selectedMarketId,
    isSingleFamily: true,
    enabled: prospectsEffectiveBuildingType === BuildingType.SINGLE_FAMILY,
  });

  const isAnyPropertySingleFamily = filteredProperties.some((property) => property.aggregateStats.isSingleFamily);

  const pageViewsEffectiveBuildingType = allSingleFamily ? BuildingType.SINGLE_FAMILY : pageViewsBuildingType;
  const impressionsEffectiveBuildingType = allSingleFamily ? BuildingType.SINGLE_FAMILY : impressionsBuildingType;
  const activePropertiesEffectiveBuildingType = allSingleFamily
    ? BuildingType.SINGLE_FAMILY
    : activePropertiesBuildingType;

  const hasSelectedProperty = selectedFilters.listingAlias !== '';

  const mfProspectsData = useMemo(
    () =>
      generateProspectsData({
        filteredProperties,
        buildingType: BuildingType.MULTIFAMILY,
        regionPerformanceData: mfRegionPerformance ?? undefined,
        packageFilter: selectedFilters.package,
        hasSelectedProperty,
      }),
    [filteredProperties, mfRegionPerformance, selectedFilters.package, hasSelectedProperty],
  );

  const sfProspectsData = useMemo(
    () =>
      generateProspectsData({
        filteredProperties,
        buildingType: BuildingType.SINGLE_FAMILY,
        regionPerformanceData: sfRegionPerformance ?? undefined,
        packageFilter: selectedFilters.package,
        hasSelectedProperty,
      }),
    [filteredProperties, sfRegionPerformance, selectedFilters.package, hasSelectedProperty],
  );

  const mfProspectsBreakdownData = useMemo(
    () =>
      generateProspectsPackageBreakdownData({
        filteredProperties,
        buildingType: BuildingType.MULTIFAMILY,
        packageFilter: selectedFilters.package,
        hasSelectedProperty,
      }),
    [filteredProperties, selectedFilters.package, hasSelectedProperty],
  );

  const sfProspectsBreakdownData = useMemo(
    () =>
      generateProspectsPackageBreakdownData({
        filteredProperties,
        buildingType: BuildingType.SINGLE_FAMILY,
        packageFilter: selectedFilters.package,
        hasSelectedProperty,
      }),
    [filteredProperties, selectedFilters.package, hasSelectedProperty],
  );

  const mfPageViewsData = useMemo(
    () =>
      generatePageViewsData({
        filteredProperties,
        buildingType: BuildingType.MULTIFAMILY,
        regionPerformanceData: mfRegionPerformance ?? undefined,
        packageFilter: selectedFilters.package,
        hasSelectedProperty,
      }),
    [filteredProperties, mfRegionPerformance, selectedFilters.package, hasSelectedProperty],
  );

  const sfPageViewsData = useMemo(
    () =>
      generatePageViewsData({
        filteredProperties,
        buildingType: BuildingType.SINGLE_FAMILY,
        regionPerformanceData: sfRegionPerformance ?? undefined,
        packageFilter: selectedFilters.package,
        hasSelectedProperty,
      }),
    [filteredProperties, sfRegionPerformance, selectedFilters.package, hasSelectedProperty],
  );

  const mfImpressionsData = useMemo(
    () =>
      generateImpressionsData({
        filteredProperties,
        buildingType: BuildingType.MULTIFAMILY,
        regionPerformanceData: mfRegionPerformance ?? undefined,
        packageFilter: selectedFilters.package,
        hasSelectedProperty,
      }),
    [filteredProperties, mfRegionPerformance, selectedFilters.package, hasSelectedProperty],
  );

  const sfImpressionsData = useMemo(
    () =>
      generateImpressionsData({
        filteredProperties,
        buildingType: BuildingType.SINGLE_FAMILY,
        regionPerformanceData: sfRegionPerformance ?? undefined,
        packageFilter: selectedFilters.package,
        hasSelectedProperty,
      }),
    [filteredProperties, sfRegionPerformance, selectedFilters.package, hasSelectedProperty],
  );

  const mfActivePropertiesData = useMemo(
    () =>
      generateActivePropertiesData({
        filteredProperties,
        buildingType: BuildingType.MULTIFAMILY,
        regionPerformanceData: mfRegionPerformance ?? undefined,
        packageFilter: selectedFilters.package,
        hasSelectedProperty,
      }),
    [filteredProperties, mfRegionPerformance, selectedFilters.package, hasSelectedProperty],
  );

  const sfActivePropertiesData = useMemo(
    () =>
      generateActivePropertiesData({
        filteredProperties,
        buildingType: BuildingType.SINGLE_FAMILY,
        regionPerformanceData: sfRegionPerformance ?? undefined,
        packageFilter: selectedFilters.package,
        hasSelectedProperty,
      }),
    [filteredProperties, sfRegionPerformance, selectedFilters.package, hasSelectedProperty],
  );

  const graphConfigs = getOverviewGraphConfigs();

  const handleExport = async (fileType: FileType, selectedGraphs: SelectedGraphs) => {
    if (user) {
      const emitMap = { xls: emitOverviewExportEvent, pdf: emitOverviewExportPdfEvent } as const;
      emitMap[fileType]?.({ user });
    }

    try {
      switch (fileType) {
        case 'xls': {
          const graphsData = extractOverviewData(
            selectedGraphs,
            {
              mfProspectsData,
              sfProspectsData,
              mfPageViewsData,
              sfPageViewsData,
              mfImpressionsData,
              sfImpressionsData,
              mfActivePropertiesData,
              sfActivePropertiesData,
              prospectsBuildingType,
              prospectsDisplayType,
            },
            isAnyPropertySingleFamily,
            allSingleFamily,
          );
          if (graphsData.length === 0) {
            enqueueToast(
              <Toast tone="critical">
                <Text>No data to export.</Text>
              </Toast>,
            );
            return;
          }
          await downloadExcel(graphsData, 'Overview');
          break;
        }
        case 'pdf': {
          const graphImages = await captureGraphs('Overview', selectedGraphs);

          const tableData = extractOverviewData(
            selectedGraphs,
            {
              mfProspectsData,
              sfProspectsData,
              mfPageViewsData,
              sfPageViewsData,
              mfImpressionsData,
              sfImpressionsData,
              mfActivePropertiesData,
              sfActivePropertiesData,
              prospectsBuildingType,
              prospectsDisplayType,
            },
            isAnyPropertySingleFamily,
            allSingleFamily,
          );

          const pdfGraphPages = graphImages.map((graph) => {
            const normalizedGraphName = graph.name.toLowerCase().replace(/^total\s+/, '');
            const matchingTable = tableData.find((td) => {
              const normalizedTableName = td.name
                .toLowerCase()
                .replace(/_/g, ' ')
                .replace(/\s+(multifamily|single family)$/, '');
              return (
                normalizedTableName === normalizedGraphName ||
                normalizedTableName.startsWith(normalizedGraphName) ||
                normalizedGraphName.startsWith(normalizedTableName)
              );
            });
            return {
              name: graph.name,
              imageData: graph.imageData,
              tableData: matchingTable,
              legend: graph.legend,
              description: graph.description,
            };
          });

          if (pdfGraphPages.length === 0) {
            enqueueToast(
              <Toast tone="critical">
                <Text>No data to export.</Text>
              </Toast>,
            );
            return;
          }

          const coverPageData = await buildCoverPageData(
            'Performance overview',
            selectedFilters,
            regionsMapping,
            filteredProperties,
          );
          await downloadPDF(pdfGraphPages, 'Overview', [], coverPageData);
          break;
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to export data.';
      enqueueToast(
        <Toast tone="critical">
          <Text>{errorMessage}</Text>
        </Toast>,
      );
      throw error;
    }
  };

  return (
    <CustomerAnalyticsPage
      header="Portfolio performance overview"
      subheader="Source: Zillow internal data"
      sidebarTitle="Listing quality opportunities"
      sidebar={<OverviewReportSidebar />}
      tabName="Overview"
      graphConfigs={graphConfigs}
      onExport={handleExport}
    >
      <Box css={{ display: 'flex', flexDirection: 'column', gap: '800' }}>
        <Prospects
          selectedRegionId={derivedSelectedMarketId}
          chartData={prospectsEffectiveBuildingType === BuildingType.SINGLE_FAMILY ? sfProspectsData : mfProspectsData}
          isRegionPerformanceLoading={
            prospectsEffectiveBuildingType === BuildingType.SINGLE_FAMILY
              ? isSfRegionPerformanceLoading
              : isMfRegionPerformanceLoading
          }
          buildingTypeFilter={prospectsBuildingType}
          setBuildingTypeFilter={setProspectsBuildingType}
          displayType={prospectsDisplayType}
          setDisplayType={setProspectsDisplayType}
          packageBreakdownData={
            prospectsEffectiveBuildingType === BuildingType.SINGLE_FAMILY
              ? sfProspectsBreakdownData
              : mfProspectsBreakdownData
          }
        />
        <PageViews
          selectedRegionId={derivedSelectedMarketId}
          chartData={pageViewsEffectiveBuildingType === BuildingType.SINGLE_FAMILY ? sfPageViewsData : mfPageViewsData}
          isRegionPerformanceLoading={
            pageViewsEffectiveBuildingType === BuildingType.SINGLE_FAMILY
              ? isSfRegionPerformanceLoading
              : isMfRegionPerformanceLoading
          }
          buildingTypeFilter={pageViewsBuildingType}
          setBuildingTypeFilter={setPageViewsBuildingType}
          displayType={pageViewsDisplayType}
          setDisplayType={setPageViewsDisplayType}
        />
        <Impressions
          selectedRegionId={derivedSelectedMarketId}
          chartData={
            impressionsEffectiveBuildingType === BuildingType.SINGLE_FAMILY ? sfImpressionsData : mfImpressionsData
          }
          isRegionPerformanceLoading={
            impressionsEffectiveBuildingType === BuildingType.SINGLE_FAMILY
              ? isSfRegionPerformanceLoading
              : isMfRegionPerformanceLoading
          }
          buildingTypeFilter={impressionsBuildingType}
          setBuildingTypeFilter={setImpressionsBuildingType}
          displayType={impressionsDisplayType}
          setDisplayType={setImpressionsDisplayType}
        />
        <ActiveProperties
          selectedRegionId={derivedSelectedMarketId}
          chartData={
            activePropertiesEffectiveBuildingType === BuildingType.SINGLE_FAMILY
              ? sfActivePropertiesData
              : mfActivePropertiesData
          }
          isRegionPerformanceLoading={
            activePropertiesEffectiveBuildingType === BuildingType.SINGLE_FAMILY
              ? isSfRegionPerformanceLoading
              : isMfRegionPerformanceLoading
          }
          regionPerformanceData={
            activePropertiesEffectiveBuildingType === BuildingType.SINGLE_FAMILY
              ? (sfRegionPerformance ?? undefined)
              : (mfRegionPerformance ?? undefined)
          }
          buildingTypeFilter={activePropertiesBuildingType}
          setBuildingTypeFilter={setActivePropertiesBuildingType}
          displayType={activePropertiesDisplayType}
          setDisplayType={setActivePropertiesDisplayType}
        />
        <FunnelBreakdown />
      </Box>
    </CustomerAnalyticsPage>
  );
};
